import type { Activity } from '../domain/types';
import { useApp } from '../state/store';
import { navigate } from '../lib/router';
import { C, HEALTH_COLORS, R, font } from '../theme/tokens';
import { nextAction, progress, roleOf } from '../state/selectors';
import { countdown, dayLabel, timeIn, ymdIn } from '../lib/dates';
import { useNow } from '../lib/useNow';
import { Avatar, Btn, Eyebrow, Mono, Photo, ProgressBar, StageTag } from './ui';
import { Sticker } from './chrome';

/** Cover card for an activity the ambassador is accountable for (Home carousel). */
export function CommitmentCard({ activity: a, single }: { activity: Activity; single: boolean }) {
  const { state: { data }, act } = useApp();
  const nowMs = useNow();
  const me = data.ambassadors[data.me];
  const role = roleOf(data, a.id) ?? '';
  const p = progress(data, a.id);
  const next = nextAction(data, a.id);
  const health = HEALTH_COLORS[a.health][0];
  const meta = `${dayLabel(ymdIn(a.startsAt, a.timeZone))} · ${a.allDay ? 'All day' : timeIn(a.startsAt, a.timeZone)} · ${a.place}`.toUpperCase();
  const today = ymdIn(nowMs, me.timeZone);
  const lead = a.organiserId === data.me;
  const team = a.team.map((m) => data.ambassadors[m.ambassadorId]).filter(Boolean);
  const share = () => (a.shareKitId ? act.openSheet({ kind: 'quickShare', kitId: a.shareKitId }) : act.toast('No approved share kit for this activity yet'));

  return (
    <article style={{ position: 'relative', flex: single ? '0 0 100%' : '0 0 88%', scrollSnapAlign: 'start', display: 'flex', flexDirection: 'column' }}>
      <button type="button" onClick={() => navigate({ name: 'activity', id: a.id })} aria-label={`Open ${a.name}`}
        style={{ display: 'block', width: '100%', position: 'relative', height: 250, border: 0, padding: 0, borderRadius: '24px 24px 0 0', overflow: 'hidden', background: C.night, textAlign: 'left' }}>
        <span style={{ position: 'absolute', inset: 0 }}><Photo id={a.photo} eager={lead} pad={40} fallback={a.series} sizes="(max-width: 480px) 88vw, 420px" /></span>
        <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(11,11,15,.15) 0%,rgba(11,11,15,0) 35%,rgba(11,11,15,.9) 100%)' }} />
        <span style={{ position: 'absolute', top: 14, left: 14, display: 'flex', gap: 6 }}>
          <StageTag stage={a.stage} style={{ background: C.white, color: C.ink, borderRadius: R.pill, padding: '7px 10px', fontSize: '0.625rem' }} />
          <span style={{ background: C.white, color: health, font: font(700, 10), letterSpacing: '.14em', padding: '7px 10px', borderRadius: R.pill, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: health }} />{a.health.toUpperCase()}
          </span>
        </span>
        <span style={{ position: 'absolute', left: 18, right: 18, bottom: 16, color: C.white, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ font: font(600, 10), letterSpacing: '.26em' }}>{(a.label ?? `${a.type} · ${a.series} UNITS`).toUpperCase()}</span>
          <span style={{ font: font(700, 38, 0.92), letterSpacing: '-.045em' }}>{a.name}</span>
          <Mono color={C.metal}>{meta}</Mono>
        </span>
      </button>
      {a.isHunt && a.stage < 3 && (
        <Sticker style={{ top: -14, right: 4, transform: 'rotate(8deg)' }}>
          GOES LIVE<br /><span style={{ font: font(700, 15, 1.1, 'mono'), letterSpacing: '-.02em' }}>{countdown(a.startsAt, nowMs)}</span>
        </Sticker>
      )}
      <div style={{ background: C.ink, color: C.white, borderRadius: '0 0 24px 24px', padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Eyebrow color={C.rim} spacing={0.24} style={{ fontSize: '0.5625rem' }}>Your role</Eyebrow>
            <span style={{ font: font(600, 15, 1.3) }}>{role}{lead ? ` · ${me.title}` : ''}</span>
          </div>
          <div style={{ display: 'flex', paddingLeft: 8 }}>
            {team.slice(0, 5).map((t) => <span key={t.id} style={{ marginLeft: -8 }}><Avatar person={t} size={28} ring={C.ink} /></span>)}
          </div>
        </div>
        <div style={{ background: C.dark2, borderRadius: R.panel, padding: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Eyebrow color={C.rim} style={{ fontSize: '0.5625rem' }}>Your next action</Eyebrow>
          <span style={{ font: font(700, 19, 1.2), letterSpacing: '-.02em' }}>{next ? `${next.title}.` : 'Your tasks are done. Post an update for the team.'}</span>
          <Mono color={C.mutedDark}>{next ? `${next.due === today ? 'DUE TODAY · ' : 'DUE '}${dayLabel(next.due).toUpperCase()}` : `TEAM HAS ${p.total - p.done} OPEN`}</Mono>
        </div>
        {p.total > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Mono color={C.mutedDark}>ACTIVITY PROGRESS</Mono><Mono color={C.white}>{`${p.done} / ${p.total} tasks`}</Mono>
            </div>
            <ProgressBar ratio={p.ratio} dark label={`${a.name} progress`} />
          </div>
        )}
        <Btn variant="primary" size="lg" full onClick={() => act.openSheet({ kind: 'update', activityId: a.id })}>Update progress</Btn>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 6 }}>
          <Btn variant="ghostDark" onClick={() => navigate({ name: 'activity', id: a.id })} style={{ padding: 0 }}>View tasks</Btn>
          <Btn variant="ghostDark" onClick={() => navigate({ name: 'collab', id: a.id })} style={{ padding: 0 }}>Collaborate</Btn>
          <Btn variant="ghostDark" onClick={share} style={{ padding: 0 }}>Share ↗</Btn>
        </div>
      </div>
    </article>
  );
}
